import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import ButtonStyled from "../../../../components/ButtonStyled";

import expressunion from "../../../../assets/images/expressunion.png";
import mtn from "../../../../assets/images/mtn.png";
import paypalIcon from "../../../../assets/images/paypal.png";
import orangeV2 from "../../../../assets/images/orangeV2.png";
import smobilpay from "../../../../assets/images/smobilpay.png";
import yoomee from "../../../../assets/images/yoomee.png";

import { countryList } from "../../../../constants";
import useTranslation from "../../../../hooks/translations";
import palette from "../../../../styles/palette";
import { CREDIT_CARD, MOBILE_MONEY, PAYPAL } from "../../../../constants/data";
import { handleOptionClick } from "../../../../utils/handleSelectChange";
import PaymentOption from "../../../../components/PaymentOption";
import PaymentSummary from "../../../../components/PaymentSummary";
import {
  getProviderServiceCharge,
  getProviderTotalAmount,
  getSelectedPaymentOption,
} from "../../../../utils/paymentCharges";
import CyberSourceMicroform from "../../../../components/CyberSourceMicroform";
import NameSection from "./NameSection";
import PhoneSection from "./PhoneSection";
import ProviderSection from "./ProviderSection";
import CountrySection from "./CountrySection";
import EmailSection from "./EmailSection";
import { appConfig } from "../../../../config";
import { getContrastTextColor } from "../../../../utils/colorManager";
import { getAvailableCountries } from "../../../../utils/getAvailableCountries";
import { Countries, Country, PaymentProviderData } from "../../../../types";

interface FormsProps {
  formik: any;
  payment: PaymentProviderData[];
  setPaymentProviderId: any;
  setPricingId: any;
  setPaymentProviderName: any;
  radioChecked: boolean;
  setRadioChecked: (checked: boolean) => void;
  paymentProviderName: string;
  checked: boolean;
  handleChecked: () => void;
  theme: any;
  providerType: string;
  setProviderType: (type: string) => void;
  onCyberSourcePay: (token: string) => void;
  amount?: number | null;
  orderTotalAmount?: number | null;
  currency?: string | null;
}

const Forms: React.FC<FormsProps> = ({
  formik,
  payment,
  setPaymentProviderId,
  setPricingId,
  setPaymentProviderName,
  radioChecked,
  setRadioChecked,
  paymentProviderName,
  checked,
  handleChecked,
  theme,
  providerType,
  setProviderType,
  onCyberSourcePay,
  amount,
  orderTotalAmount,
  currency,
}) => {
  const { t } = useTranslation();

  const { isValidating, isValid } = formik;
  const isSubmitDisabled = isValidating || !isValid || !checked;
  const isPaypal = formik?.values?.providerType === PAYPAL;
  const isCard = formik?.values?.providerType === CREDIT_CARD;
  const isMobileMoney = formik?.values?.providerType === MOBILE_MONEY;

  const selectedProviderId = formik?.values?.paymentProviderId?.uuid;
  const selectedPaymentData = getSelectedPaymentOption(
    payment,
    selectedProviderId,
  );

  const orderAmount = Number(orderTotalAmount) || 0;
  const expandedProvider = payment.find(
    (p: any) => p.name === formik?.values?.providerType,
  );
  const summaryServiceCharge = expandedProvider
    ? getProviderServiceCharge(expandedProvider, selectedProviderId)
    : 0;
  const summaryTotal = expandedProvider
    ? getProviderTotalAmount(expandedProvider, selectedProviderId)
    : null;
  // Mobile Money and Card charges depend on the chosen provider, so only
  // surface the summary once one is selected; other methods can show it on
  // expand. (Single-card options are pre-selected on expand, so they still
  // show immediately.)
  const showPaymentSummary =
    isMobileMoney || isCard
      ? Boolean(selectedPaymentData)
      : Boolean(formik?.values?.providerType);

  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<
    string | null
  >(null);
  const [availableCountries, setAvailableCountries] = useState<any[]>([]);
  const [selectedCardOption, setSelectedCardOption] = useState<any>(null);

  const cardPaymentProvider = payment.find((p: any) => p.name === CREDIT_CARD);
  const cardOptions: any[] = cardPaymentProvider?.paymentOptions ?? [];
  const isSingleCyberSource =
    cardOptions.length === 1 &&
    cardOptions[0]?.paymentProviderInfo?.name === "cybersource";

  const mobileMoney = payment.find((p: any) => p.name === "Mobile Money");

  useEffect(() => {
    const countries: Countries[] | undefined = mobileMoney?.paymentOptions.map(
      (opt: any) => ({
        countryCode: opt.countryCode,
        name: { en: opt.names?.en },
      }),
    );
    if (mobileMoney && mobileMoney.paymentOptions) {
      const available = getAvailableCountries(countries);
      setAvailableCountries(available);
    }
  }, [mobileMoney]);

  const defaultValue = countryList.find((item) => item?.name === "Cameroon");

  const onChangeCountry = (country: Country) => {
    setSelectedOption(country);
  };

  useEffect(() => {
    if (!isCard) setSelectedCardOption(null);
  }, [isCard]);

  const handleClickOption = (option: string) => {
    if (selectedPaymentOption === option) {
      setSelectedPaymentOption(null);
      return null;
    } else {
      setSelectedPaymentOption(option);
      return option;
    }
  };

  const handleCardSubOptionClick = async (option: any) => {
    const info = option.paymentProviderInfo;
    setSelectedCardOption(option);
    await formik.setFieldValue("paymentProviderId", { uuid: info.id.uuid });
    setPaymentProviderId({ uuid: info.id.uuid });
    await formik.setFieldValue("customerPricingId", {
      uuid: info.customerPricingId.uuid,
    });
    setPricingId(info.customerPricingId.uuid);
    if (info.name === "flocash") {
      await formik.handleSubmit(formik.values);
    }
  };

  const translatedPaymentOptions: any = {
    Paypal: t("paypal"),
    "Mobile Money": t("mobileMoney"),
    Card: t("creditCard"),
  };

  const terms = (
    <React.Fragment>
      <Typography sx={{ fontSize: "13px" }}>
        {t("agreeTo")}
        <Link
          underline="none"
          color="#4784F9"
          href="https://enkap.cm/privacy-policy/?lang=en"
        >
          {` ${t("termsAndCondition")} `}
        </Link>
        {t("and")}
        <Link
          underline="none"
          color="#4784F9"
          href="https://enkap.cm/privacy-policy/?lang=en"
        >
          {` ${t("privacyPolicy")}`}
        </Link>
      </Typography>
    </React.Fragment>
  );

  useEffect(() => {
    if (defaultValue && !selectedOption) {
      setSelectedOption(defaultValue);
    }
  }, [defaultValue, selectedOption]);

  return (
    <div className={"formSection"}>
      <Grid className={"formHeader"} container direction="column">
        <Typography variant="h6" style={{ fontWeight: 600, color: "#2E2E2E" }}>
          {t("paymentMethod")}
        </Typography>
        <Typography
          style={{ fontWeight: 100, fontSize: "16px", color: "#2E2E2E" }}
        >
          {t("serviceChargeNotification")}
        </Typography>
      </Grid>
      <form onSubmit={formik.handleSubmit}>
        <Grid container direction="column" className={"rootForm"}>
          <Grid item>
            <FormControl className={"fieldset"} component="fieldset" fullWidth>
              {payment?.map((paymentProvider: any) => {
                if (
                  !paymentProvider.paymentOptions ||
                  paymentProvider.paymentOptions.length === 0
                ) {
                  return null;
                }
                return (
                  appConfig.ACTIVATE_CREDIT_CARD && (
                    <PaymentOption
                      key={paymentProvider.id}
                      paymentProvider={paymentProvider}
                      onClick={handleOptionClick}
                      optionChangeObject={{
                        formik,
                        setPaymentProviderId,
                        setPricingId,
                        setProviderType,
                        handleClickOption,
                        providerType,
                        setRadioChecked,
                      }}
                      dataTestId={`payment-option-${paymentProvider.name}`}
                      selectedProviderId={selectedProviderId}
                      currency={currency}
                    >
                      {selectedPaymentOption && providerType ? (
                        <Grid container direction="column">
                          {/* Service charge / order amount / total to pay */}
                          {showPaymentSummary && (
                            <PaymentSummary
                              serviceCharge={summaryServiceCharge ?? 0}
                              orderAmount={orderAmount}
                              total={summaryTotal}
                              currency={currency}
                            />
                          )}

                          {/* Card sub-options (flocash + cybersource) */}
                          {isCard &&
                            (isSingleCyberSource ? (
                              // Single cybersource: render microform directly
                              <CyberSourceMicroform
                                onPay={onCyberSourcePay}
                                disabled={!checked}
                                theme={theme}
                                amount={amount}
                                currency={currency}
                              />
                            ) : (
                              // Multiple options: show sub-option picker
                              <Grid container direction="column" sx={{ mt: 1 }}>
                                {cardOptions.map((option: any) => {
                                  const info = option.paymentProviderInfo;
                                  const isSelected =
                                    selectedCardOption?.paymentProviderInfo?.id
                                      ?.uuid === info.id.uuid;
                                  const isCyberSourceOption =
                                    info.name === "cybersource";
                                  return (
                                    <React.Fragment key={info.id.uuid}>
                                      <Box
                                        onClick={() =>
                                          handleCardSubOptionClick(option)
                                        }
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          px: 1.5,
                                          py: 1,
                                          mb: 0.5,
                                          border: "1px solid",
                                          borderColor: isSelected
                                            ? "primary.main"
                                            : "#E0E0E0",
                                          borderRadius: "8px",
                                          cursor: "pointer",
                                          backgroundColor: isSelected
                                            ? "rgba(25, 118, 210, 0.04)"
                                            : "transparent",
                                          "&:hover": {
                                            backgroundColor: "#F5F5F5",
                                          },
                                        }}
                                      >
                                        <Typography
                                          sx={{ flexGrow: 1, fontSize: 13 }}
                                        >
                                          {info.nameExternal}
                                        </Typography>
                                        <KeyboardArrowRightIcon
                                          fontSize="small"
                                          color="action"
                                        />
                                      </Box>
                                      {isSelected && isCyberSourceOption && (
                                        <CyberSourceMicroform
                                          onPay={onCyberSourcePay}
                                          disabled={!checked}
                                          theme={theme}
                                          amount={amount}
                                        />
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </Grid>
                            ))}

                          {/* Country — Mobile Money only */}
                          {formik?.values?.providerType === MOBILE_MONEY && (
                            <CountrySection
                              formik={formik}
                              onChangeCountry={onChangeCountry}
                              selectedOption={selectedOption}
                              availableCountries={availableCountries}
                            />
                          )}

                          {/* Providers */}
                          {formik?.values?.providerType &&
                            payment &&
                            !isPaypal &&
                            !isCard && (
                              <ProviderSection
                                payment={payment}
                                formik={formik}
                                providerType={providerType}
                                setPaymentProviderId={setPaymentProviderId}
                                setPricingId={setPricingId}
                                radioChecked={radioChecked}
                                setRadioChecked={setRadioChecked}
                                setPaymentProviderName={setPaymentProviderName}
                                country={selectedOption}
                              />
                            )}

                          {/* Phone number */}
                          {!isPaypal && !isCard && (
                            <PhoneSection
                              formik={formik}
                              paymentProviderName={paymentProviderName}
                              disabled={radioChecked}
                              providerType={providerType}
                              country={selectedOption}
                            />
                          )}

                          {/* Submit button — Card handles its own sub-option actions */}
                          {formik?.values?.providerType && !isCard && (
                            <Grid
                              item
                              className={"inputWrapper"}
                              sx={{ marginTop: 2 }}
                            >
                              {isPaypal && (
                                <Typography
                                  style={{
                                    fontSize: "0.85rem",
                                  }}
                                  mb={1}
                                >
                                  {t("paypalRedirection")}
                                </Typography>
                              )}
                              <ButtonStyled
                                className="submitButton"
                                disabled={isSubmitDisabled}
                                type="submit"
                                label={isPaypal ? "" : t("paymentButton")}
                                icon={
                                  isPaypal && (
                                    <img src={paypalIcon} alt="paymentIcon" />
                                  )
                                }
                                style={{
                                  width: "100%",
                                  height: 38,
                                  backgroundColor: isSubmitDisabled
                                    ? theme?.primaryColor
                                    : theme?.secondaryColor
                                      ? isPaypal
                                        ? palette.orange
                                        : theme?.secondaryColor
                                      : isPaypal
                                        ? palette.orange
                                        : palette.primary.main,
                                  color:
                                    getContrastTextColor(
                                      theme?.secondaryColor,
                                    ) === "light"
                                      ? palette.text.light
                                      : palette.black,
                                }}
                              />
                            </Grid>
                          )}
                        </Grid>
                      ) : (
                        <></>
                      )}
                    </PaymentOption>
                  )
                );
              })}
              {
                <Grid
                  className={"checkBoxWrapper"}
                  container
                  direction="column"
                >
                  {!checked && (
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#d32f2f",
                        marginBottom: 4,
                      }}
                    >
                      <WarningAmberRoundedIcon
                        sx={{ marginRight: 1, fontSize: 20 }}
                      />
                      <Typography
                        style={{
                          fontSize: "0.75rem",
                        }}
                      >
                        {t("paymentTerms")}
                      </Typography>
                    </Box>
                  )}

                  <Typography sx={{ marginTop: 2, marginLeft: 1 }}>
                    {terms}
                  </Typography>
                </Grid>
              }
            </FormControl>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};
export default Forms;

const Label = ({ name, onClick }: { name: any; onClick: any }) => {
  let logo = [
    { logo: mtn, name: "mtn" },
    { logo: orangeV2, name: "orangeV2" },
    { logo: smobilpay, name: "smobilpay" },
    { logo: expressunion, name: "expressunion" },
    { logo: yoomee, name: "yoomee" },
  ];
  let image = logo.find((i) => (i.name === name ? i.logo : null));
  return <img onClick={onClick} src={image?.logo} alt={name} loading="lazy" />;
};
